import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import shopifyApi from '../services/shopifyApi';

const ShopifyCartContext = createContext(null);

export const ShopifyCartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [cartId, setCartId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart ID from localStorage on mount
  useEffect(() => {
    const storedCartId = shopifyApi.storage.getCartId();
    if (storedCartId) {
      setCartId(storedCartId);
      fetchCart(storedCartId);
    }
  }, []);

  // Fetch cart data
  const fetchCart = useCallback(async (id) => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await shopifyApi.cart.get(id);
      
      if (response.cart) {
        setCart(response.cart);
        setError(null);
      } else if (response.errors) {
        // Cart might be expired, clear it
        shopifyApi.storage.clearCartId();
        setCartId(null);
        setCart(null);
      }
    } catch (err) {
      console.error('Error fetching cart:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new cart
  const createCart = useCallback(async (lines = [], options = {}) => {
    try {
      setLoading(true);
      setError(null);

      const response = await shopifyApi.cart.create(lines, options);
      
      if (response.cartCreate?.cart) {
        const newCart = response.cartCreate.cart;
        setCart(newCart);
        setCartId(newCart.id);
        shopifyApi.storage.setCartId(newCart.id);
        return newCart;
      } else if (response.cartCreate?.userErrors?.length > 0) {
        const error = response.cartCreate.userErrors[0];
        throw new Error(error.message);
      }
    } catch (err) {
      console.error('Error creating cart:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Add items to cart
  const addToCart = useCallback(async ({ variant, quantity = 1, isSubscription = false, attributes = [] }) => {
    try {
      setLoading(true);
      setError(null);

      // Prepare line item
      const lines = [{
        merchandiseId: variant.id,
        quantity,
        attributes: isSubscription ? [...attributes, { key: 'subscription', value: 'true' }] : attributes,
      }];

      let currentCart = cart;
      let currentCartId = cartId;

      // Create cart if it doesn't exist
      if (!currentCartId) {
        const newCart = await createCart(lines);
        setIsCartOpen(true);
        return newCart;
      }

      // Add to existing cart
      const response = await shopifyApi.cart.addLines(currentCartId, lines);
      
      if (response.cartLinesAdd?.cart) {
        const updatedCart = response.cartLinesAdd.cart;
        setCart(updatedCart);
        setIsCartOpen(true);
        return updatedCart;
      } else if (response.cartLinesAdd?.userErrors?.length > 0) {
        const error = response.cartLinesAdd.userErrors[0];
        
        // If cart is invalid, create a new one
        if (error.code === 'INVALID') {
          shopifyApi.storage.clearCartId();
          setCartId(null);
          setCart(null);
          return await createCart(lines);
        }
        
        throw new Error(error.message);
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [cart, cartId, createCart]);

  // Update cart line quantity
  const updateQuantity = useCallback(async (lineId, quantity) => {
    if (!cartId) return;

    try {
      setLoading(true);
      setError(null);

      const lines = [{
        id: lineId,
        quantity,
      }];

      const response = await shopifyApi.cart.updateLines(cartId, lines);
      
      if (response.cartLinesUpdate?.cart) {
        setCart(response.cartLinesUpdate.cart);
        return response.cartLinesUpdate.cart;
      } else if (response.cartLinesUpdate?.userErrors?.length > 0) {
        const error = response.cartLinesUpdate.userErrors[0];
        throw new Error(error.message);
      }
    } catch (err) {
      console.error('Error updating quantity:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [cartId]);

  // Remove item from cart
  const removeFromCart = useCallback(async (lineId) => {
    if (!cartId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await shopifyApi.cart.removeLines(cartId, [lineId]);
      
      if (response.cartLinesRemove?.cart) {
        setCart(response.cartLinesRemove.cart);
        return response.cartLinesRemove.cart;
      } else if (response.cartLinesRemove?.userErrors?.length > 0) {
        const error = response.cartLinesRemove.userErrors[0];
        throw new Error(error.message);
      }
    } catch (err) {
      console.error('Error removing from cart:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [cartId]);

  // Apply discount code
  const applyDiscount = useCallback(async (discountCode) => {
    if (!cartId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await shopifyApi.cart.applyDiscount(cartId, [discountCode]);
      
      if (response.cartDiscountCodesUpdate?.cart) {
        setCart(response.cartDiscountCodesUpdate.cart);
        return response.cartDiscountCodesUpdate.cart;
      } else if (response.cartDiscountCodesUpdate?.userErrors?.length > 0) {
        const error = response.cartDiscountCodesUpdate.userErrors[0];
        throw new Error(error.message);
      }
    } catch (err) {
      console.error('Error applying discount:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [cartId]);

  // Update buyer identity
  const updateBuyerIdentity = useCallback(async (buyerIdentity) => {
    if (!cartId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await shopifyApi.cart.updateBuyerIdentity(cartId, buyerIdentity);
      
      if (response.cartBuyerIdentityUpdate?.cart) {
        setCart(response.cartBuyerIdentityUpdate.cart);
        return response.cartBuyerIdentityUpdate.cart;
      } else if (response.cartBuyerIdentityUpdate?.userErrors?.length > 0) {
        const error = response.cartBuyerIdentityUpdate.userErrors[0];
        throw new Error(error.message);
      }
    } catch (err) {
      console.error('Error updating buyer identity:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [cartId]);

  // Clear cart
  const clearCart = useCallback(() => {
    shopifyApi.storage.clearCartId();
    setCartId(null);
    setCart(null);
    setError(null);
  }, []);

  // Calculate cart totals
  const cartTotals = useCallback(() => {
    if (!cart) {
      return {
        itemCount: 0,
        subtotal: 0,
        total: 0,
        currency: 'EUR',
      };
    }

    const itemCount = cart.totalQuantity || 0;
    const subtotal = parseFloat(cart.cost?.subtotalAmount?.amount || 0);
    const total = parseFloat(cart.cost?.totalAmount?.amount || 0);
    const currency = cart.cost?.totalAmount?.currencyCode || 'EUR';

    return {
      itemCount,
      subtotal,
      total,
      currency,
    };
  }, [cart]);

  // Get checkout URL
  const getCheckoutUrl = useCallback(() => {
    return cart?.checkoutUrl || null;
  }, [cart]);

  // Redirect to checkout
  const redirectToCheckout = useCallback(() => {
    const checkoutUrl = getCheckoutUrl();
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  }, [getCheckoutUrl]);

  const value = {
    cart,
    cartId,
    loading,
    error,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    updateQuantity,
    removeFromCart,
    applyDiscount,
    updateBuyerIdentity,
    clearCart,
    cartTotals: cartTotals(),
    getCheckoutUrl,
    redirectToCheckout,
    refreshCart: () => fetchCart(cartId),
  };

  return (
    <ShopifyCartContext.Provider value={value}>
      {children}
    </ShopifyCartContext.Provider>
  );
};

export const useShopifyCart = () => {
  const context = useContext(ShopifyCartContext);
  if (!context) {
    throw new Error('useShopifyCart must be used within a ShopifyCartProvider');
  }
  return context;
};

export default ShopifyCartContext;